import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Audience,
  Campaign,
  Status_Enum as CampaignStatus,
  CampaignType_Enum as CampaignType,
  CampaignUsage,
  CountSegmentRecipientsRequest,
  ListRecipientsRequest_EngagementFilter as EngagementFilter,
  GetAudienceRequest,
  GetCampaignRequest,
  GetCampaignsSummaryRequest,
  GetCampaignUsageRequest,
  GetListRequest,
  GetUsageRequest,
  ListActiveYearsRequest,
  ListCampaignsRequest,
  ListListsRequest,
  ListRecipientsRequest,
  ListSegmentsRequest,
  LocationUsage,
  Segment,
  ListRecipientsRequest_StatusFilter as StatusFilter,
} from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import {
  ListRecipientsRequest_AppointmentQualifier as MetlAppointmentQualifier,
  ListRecipientsRequest as MetlListRecipientsRequest,
} from '@weave/schema-gen-ts/dist/schemas/messaging/etl/bulk/v1/service.pb';
import {
  FilterOptions_AppointmentQualifier as AppointmentQualifier,
  FilterOptions,
} from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/bulk.pb';
import { SortDirection } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { useInfiniteQuery, UseInfiniteQueryOptions, useQuery, useQueryClient, UseQueryOptions } from 'react-query';
import { SchemaBulkMetl } from '@frontend/schema';
import { LIST_RECIPIENTS_DEFAULT_PAGE_SIZE } from './constants';
import { SchemaBulkMessagingService } from './service';
import { sortCampaigns } from './utils';

const defaultOptions = {
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  retry: (failureCount: number, error: any) => {
    // Don't retry client errors (4xx). These are user/request issues.
    if (error?.status >= 400 && error?.status < 500) {
      return false;
    }
    // Retry network/server errors (5xx) once
    return failureCount < 1;
  },
} as const satisfies Partial<UseQueryOptions>;

const KEY_NAMESPACE = 'bulk-messaging';
export type BulkMessagingBaseKey = readonly [typeof KEY_NAMESPACE, string];

/**
 * React Query keys for Bulk Messaging.
 *
 * This object provides a standardized way to generate React Query keys for all Bulk Messaging operations.
 * It follows a hierarchical pattern where base keys are used for cache invalidation and specific keys
 * are used for individual query instances.
 *
 * By convention the 3rd element in the key is the request object sent to the backend.
 *
 * @example
 * ```typescript
 * // Use base keys for broad cache operations
 * queryClient.invalidateQueries(keys.base.getCampaignsSummary);
 *
 * // Use specific keys for individual queries
 * const campaignQuery = useQuery({
 *   queryKey: keys.getCampaign({ campaignId: '123', orgId: 'org-456' }),
 *   queryFn: () => SchemaBulkMessagingService.GetCampaign(request)
 * });
 * ```
 */
export const keys = {
  base: {
    countSegmentRecipients: [KEY_NAMESPACE, 'count-segment-recipients'] as const,
    getAudience: [KEY_NAMESPACE, 'get-audience'] as const,
    getCampaign: [KEY_NAMESPACE, 'get-campaign'] as const,
    getCampaignUsage: [KEY_NAMESPACE, 'get-campaign-usage'] as const,
    getCampaignsSummary: [KEY_NAMESPACE, 'get-campaigns-summary'] as const,
    getList: [KEY_NAMESPACE, 'get-list'] as const,
    getUsage: [KEY_NAMESPACE, 'get-usage'] as const,
    listActiveYears: [KEY_NAMESPACE, 'list-active-years'] as const,
    listCampaigns: [KEY_NAMESPACE, 'list-campaigns'] as const,
    listDraftCampaigns: [KEY_NAMESPACE, 'list-draft-campaigns'] as const,
    listLists: [KEY_NAMESPACE, 'list-lists'] as const,
    listRecipients: [KEY_NAMESPACE, 'list-recipients'] as const,
    listSegments: [KEY_NAMESPACE, 'list-segments'] as const,
    metlListRecipients: [KEY_NAMESPACE, 'metl-list-recipients'] as const,
  },
  countSegmentRecipients: (request: CountSegmentRecipientsRequest) =>
    [...keys.base.countSegmentRecipients, request] as const,
  getAudience: (request: GetAudienceRequest) => [...keys.base.getAudience, request] as const,
  getCampaign: (request: GetCampaignRequest) => [...keys.base.getCampaign, request] as const,
  getCampaignUsage: (request: GetCampaignUsageRequest) => [...keys.base.getCampaignUsage, request] as const,
  getCampaignsSummary: (request: GetCampaignsSummaryRequest) => [...keys.base.getCampaignsSummary, request] as const,
  getList: (request: GetListRequest) => [...keys.base.getList, request] as const,
  getUsage: (request: GetUsageRequest) => [...keys.base.getUsage, request] as const,
  listActiveYears: (request: ListActiveYearsRequest) => [...keys.base.listActiveYears, request] as const,
  listCampaigns: (request: ListCampaignsRequest) => [...keys.base.listCampaigns, request] as const,
  listDraftCampaigns: (request: ListCampaignsRequest) => [...keys.base.listDraftCampaigns, request] as const,
  listLists: (request: ListListsRequest) => [...keys.base.listLists, request] as const,
  listRecipients: (request: ListRecipientsRequest) => [...keys.base.listRecipients, request] as const,
  listSegments: (request: ListSegmentsRequest) => [...keys.base.listSegments, request] as const,
  metlListRecipients: (request: MetlListRecipientsRequest) => [...keys.base.metlListRecipients, request] as const,
};

export const useCountSegmentRecipients = (
  request: CountSegmentRecipientsRequest,
  options?: UseQueryOptions<number | undefined, unknown, number | undefined>
) =>
  useQuery({
    ...defaultOptions,
    queryKey: keys.countSegmentRecipients(request),
    queryFn: async () => {
      const data = await SchemaBulkMessagingService.CountSegmentRecipients(request);
      return data.recipientCount;
    },
    ...options,
  });

// TODO: Replace onError callback by returning a promise instead
export const useExpandCampaignsSummary = (onError?: (err: unknown) => void) => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  // Stabilize onError callback
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const expandMonth = useCallback(
    async (request: GetCampaignsSummaryRequest, month: number) => {
      if (request.locationIds.length === 0) return;

      try {
        const listCampaignsRequest: ListCampaignsRequest = {
          orgId: request.orgId,
          locationIds: request.locationIds,
          statuses: [
            CampaignStatus.CANCELED,
            CampaignStatus.COMPLETED,
            CampaignStatus.FAILED,
            CampaignStatus.PROCESSING,
            CampaignStatus.SCHEDULED,
          ],
          types: request.types,
          sort: request.sort,
          year: request.year,
          month,
        };

        const campaignsSummaryQueryKey = keys.getCampaignsSummary(request);
        const listCampaignsQueryKey = keys.listCampaigns(listCampaignsRequest);

        // Check if the selected month's campaigns are already cached
        if (queryClient.getQueryData(listCampaignsQueryKey)) return;

        setIsLoading(true);

        // Get the campaign summary from the cache
        const campaignsSummary = queryClient.getQueryData<Campaign[]>(campaignsSummaryQueryKey) ?? [];

        // Fetch all campaigns for the month to expand
        // TODO: Adjust this for pagination
        const response = await SchemaBulkMessagingService.ListCampaigns(listCampaignsRequest);
        const additionalCampaigns = response.campaigns;

        // Combine and deduplicate campaigns
        const seenIds = new Set<string>();
        const uniqueCampaigns: Campaign[] = [];

        for (const campaign of [...campaignsSummary, ...additionalCampaigns]) {
          if (campaign.campaignId && !seenIds.has(campaign.campaignId)) {
            seenIds.add(campaign.campaignId);
            uniqueCampaigns.push(campaign);
          }
        }

        const updatedCampaignsSummary = sortCampaigns(uniqueCampaigns);

        // Update the cache
        queryClient.setQueryData(campaignsSummaryQueryKey, updatedCampaignsSummary);
        queryClient.setQueryData(listCampaignsQueryKey, additionalCampaigns);
      } catch (err) {
        onErrorRef.current?.(err);
      } finally {
        setIsLoading(false);
      }
    },
    [queryClient]
  );

  return { expandMonth, isLoading };
};

// TODO: Replace UseQueryOptions with helper types from the query helper library
export const useGetAudience = (request: GetAudienceRequest, options?: UseQueryOptions<Audience, unknown, Audience>) =>
  useQuery({
    ...defaultOptions,
    queryKey: keys.getAudience(request),
    queryFn: () => SchemaBulkMessagingService.GetAudience(request),
    enabled: !!request.campaignId,
    ...options,
  });

export const useGetCampaign = (request: GetCampaignRequest, options?: UseQueryOptions<Campaign, unknown, Campaign>) =>
  useQuery({
    ...defaultOptions,
    queryKey: keys.getCampaign(request),
    queryFn: () => SchemaBulkMessagingService.GetCampaign(request),
    enabled: !!request.campaignId,
    ...options,
  });

export const useGetCampaignsSummary = (
  request: GetCampaignsSummaryRequest,
  options?: UseQueryOptions<Campaign[], unknown, Campaign[]>
) =>
  useQuery({
    ...defaultOptions,
    queryKey: keys.getCampaignsSummary(request),
    queryFn: async () => {
      const data = await SchemaBulkMessagingService.GetCampaignsSummary(request);
      return data.campaigns;
    },
    enabled: !!request.locationIds.length,
    ...options,
  });

export const useGetCampaignUsage = (
  request: GetCampaignUsageRequest,
  options?: UseQueryOptions<CampaignUsage, unknown, CampaignUsage>
) =>
  useQuery({
    ...defaultOptions,
    queryKey: keys.getCampaignUsage(request),
    queryFn: () => SchemaBulkMessagingService.GetCampaignUsage(request),
    enabled: !!request.campaignId,
    ...options,
  });

export const useGetList = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (listId: string) => {
      const request: GetListRequest = { listId };
      return queryClient.fetchQuery({
        queryKey: keys.getList(request),
        queryFn: () => SchemaBulkMessagingService.GetList(request),
      });
    },
    [queryClient]
  );
};

export const useGetUsage = (
  request: GetUsageRequest,
  month?: number,
  options?: UseQueryOptions<Record<string, LocationUsage>, unknown, Record<string, LocationUsage>>
) =>
  useQuery({
    ...defaultOptions,
    queryKey: keys.getUsage(request),
    queryFn: async () => {
      const data = await SchemaBulkMessagingService.GetUsage(request);
      return data.locations;
    },
    select: (data) => {
      if (!month) return data;
      const filteredData: Record<string, LocationUsage> = {};
      Object.entries(data).forEach(([locationId, locationUsage]) => {
        filteredData[locationId] = {
          usage: [
            locationUsage.usage?.find((usage) => usage.month === month) ?? {
              allotment: 0,
              error: 0,
              month,
              scheduled: 0,
              sent: 0,
            },
          ],
        };
      });
      return filteredData;
    },
    enabled: !!request.locationIds.length,
    ...options,
  });

export const useListActiveYears = (
  request: ListActiveYearsRequest,
  options?: UseQueryOptions<number[], unknown, number[]>
) =>
  useQuery({
    ...defaultOptions,
    queryKey: keys.listActiveYears(request),
    queryFn: async () => {
      const data = await SchemaBulkMessagingService.ListActiveYears(request);
      return data.years;
    },
    select: (years) => years.sort((a, b) => b - a),
    enabled: !!request.locationIds.length,
    ...options,
  });

// TODO: Implement pagination
// TODO: Sort direction and the sort field are broken on the backend. Once that's fixed, remove client-side sorting.
// Also the backend is returning campaigns based on startSendAt instead of createdAt. It is possible to get campaigns in the wrong years.
export const useListCampaignDrafts = <TData = Campaign[]>(
  request: Pick<ListCampaignsRequest, 'locationIds' | 'orgId' | 'sort' | 'year'>,
  hasBulkEmailAccess: boolean,
  hasBulkTextAccess: boolean,
  options?: UseQueryOptions<Campaign[], unknown, TData>
) => {
  const draftsRequest: ListCampaignsRequest = {
    ...request,
    pageSize: 100,
    statuses: [CampaignStatus.DRAFT],
    types: [...(hasBulkEmailAccess ? [CampaignType.EMAIL] : []), ...(hasBulkTextAccess ? [CampaignType.SMS] : [])],
  };

  return useQuery<Campaign[], unknown, TData>({
    ...defaultOptions,
    queryKey: keys.listDraftCampaigns(draftsRequest),
    queryFn: async () => {
      const response = await SchemaBulkMessagingService.ListCampaigns(draftsRequest);
      return response.campaigns;
    },
    select: ((campaigns: Campaign[]) => {
      // TODO: Sort on the frontend since backend sorting is broken. Remove this when backend is fixed.
      const isDescending = !request.sort || request.sort === SortDirection.DESCENDING;
      return [...campaigns].sort((a, b) => {
        const dateA = new Date(a.createdAt ?? 0).getTime();
        const dateB = new Date(b.createdAt ?? 0).getTime();
        return isDescending ? dateB - dateA : dateA - dateB;
      });
    }) as any,
    enabled: !!draftsRequest.locationIds.length && !!draftsRequest.types.length,
    ...options,
  });
};

export const useListLists = (request: ListListsRequest) =>
  useInfiniteQuery({
    ...defaultOptions,
    keepPreviousData: true, // TODO: Is this needed?
    queryKey: keys.listLists(request),
    queryFn: ({ pageParam }) => SchemaBulkMessagingService.ListLists({ ...request, pageToken: pageParam }),
    getNextPageParam: (lastPage) => (lastPage.nextPageToken ? lastPage.nextPageToken : undefined),
    enabled: !!request.locationId,
  });

export const useListRecipients = (
  request: ListRecipientsRequest,
  options?: Partial<UseInfiniteQueryOptions<any, unknown, any>>
) =>
  useInfiniteQuery({
    ...defaultOptions,
    queryKey: keys.listRecipients(request),
    queryFn: async ({ pageParam }) => {
      const response = await SchemaBulkMessagingService.ListRecipients({
        ...request,
        pageToken: pageParam,
      });
      return {
        rows: response.recipients ?? [],
        nextPageToken: response.nextPageToken,
      };
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.nextPageToken) return undefined;
      const recipientCount = lastPage.rows?.length ?? 0;
      const pageSize = request.pageSize ?? LIST_RECIPIENTS_DEFAULT_PAGE_SIZE;
      if (recipientCount < pageSize) return undefined;
      return lastPage.nextPageToken;
    },
    select: (data) => ({
      pages: data.pages.map((page) => ({ rows: page.rows })),
      pageParams: data.pageParams,
    }),
    enabled: !!request.campaignId,
    ...options,
  });

export const useListSegments = (
  request: ListSegmentsRequest,
  options?: UseQueryOptions<Segment[], unknown, Segment[]>
) =>
  useQuery({
    ...defaultOptions,
    queryKey: keys.listSegments(request),
    queryFn: async () => {
      const data = await SchemaBulkMessagingService.ListSegments(request);
      return data.segments;
    },
    enabled: !!request.campaignId,
    ...options,
  });

export const useMetlListRecipients = (filters: FilterOptions, pageSize: number, searchToken?: string) => {
  const sanitizedSearchToken = searchToken?.replace(/[\t\r\n]+/g, ' ').trim();
  const request = requestFactory.metlListRecipients(filters, sanitizedSearchToken);

  return useInfiniteQuery({
    queryKey: keys.metlListRecipients(request),
    queryFn: (queryFunctionContext) =>
      SchemaBulkMetl.ListRecipients(
        {
          ...request,
          pageSize,
          lastPersonSortName: queryFunctionContext.pageParam?.lastPersonSortName,
          lastRecipientId: queryFunctionContext.pageParam?.lastRecipientId,
        },
        { signal: queryFunctionContext.signal }
      ),
    getNextPageParam: (lastPage) => {
      const recipientCount = lastPage.recipients?.length ?? 0;
      if (recipientCount < pageSize) return undefined;

      const lastRecipient = lastPage.recipients?.[lastPage.recipients.length - 1];
      if (!lastRecipient) return undefined;

      const nextPageParam = {
        lastPersonSortName: lastRecipient?.personSortName,
        lastRecipientId: lastRecipient?.recipientId,
      };
      return nextPageParam;
    },
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => ({ rows: page.recipients })), // Special formatting for the paginated list component
    }),
  });
};

const convertAppointmentQualifier = (
  qualifier: AppointmentQualifier | undefined
): MetlAppointmentQualifier | undefined => {
  if (!qualifier) return undefined;

  switch (qualifier) {
    case AppointmentQualifier.LAST_SEEN:
      return MetlAppointmentQualifier.LAST_SEEN;
    case AppointmentQualifier.NEXT_APPOINTMENT:
      return MetlAppointmentQualifier.NEXT_APPOINTMENT;
    case AppointmentQualifier.NO_APPOINTMENT:
      return MetlAppointmentQualifier.NO_APPOINTMENT;
    case AppointmentQualifier.UNSPECIFIED:
      return MetlAppointmentQualifier.UNSPECIFIED;
    default:
      return undefined;
  }
};

export const requestFactory = {
  metlListRecipients: (filters: FilterOptions, searchToken?: string): MetlListRecipientsRequest => ({
    appointmentBeginDate: filters.appointmentBeginDate,
    appointmentEndDate: filters.appointmentEndDate,
    appointmentPractitioners: filters.appointmentPractitioners,
    appointmentQualifier: convertAppointmentQualifier(filters.appointmentQualifier),
    appointmentStatuses: filters.appointmentStatuses,
    appointmentTypes: filters.appointmentTypes,
    maxAge: filters.maxAge,
    minAge: filters.minAge,
    // minDaysSinceLastBulkMessage: filters.minDaysSinceLastBulkMessage, // TODO: Implement this when the backend supports it
    minDaysSinceLastMarketingMessage: filters.minDaysSinceLastMarketingMessage,
    primaryInsurance: filters.primaryInsurances,
    recallBeginDate: filters.recallBeginDate,
    recallEndDate: filters.recallEndDate,
    recallTypes: filters.recallTypes,
    recipientStatuses: filters.recipientStatuses,
    sourceTenantIds: filters.sourceTenantIds,
    searchToken,
  }),

  listRecipients: ({
    campaignId,
    engagementFilter,
    orgId,
    pageSize,
    pageToken,
    statusFilter,
  }: PartialListRecipientsRequest): ListRecipientsRequest => ({
    campaignId,
    engagementFilter: engagementFilter ?? EngagementFilter.ALL_ENGAGEMENT_TYPES,
    orgId,
    pageSize: pageSize ?? LIST_RECIPIENTS_DEFAULT_PAGE_SIZE,
    pageToken: pageToken ?? '',
    statusFilter: statusFilter ?? StatusFilter.ALL_STATUSES,
  }),
};

type PartialListRecipientsRequest = Partial<ListRecipientsRequest> & {
  campaignId: string;
  orgId: string;
};
